import * as Sentry from "@sentry/react";

/**
 * Sanitize context data by removing Blob/File objects that Sentry can't serialize
 */
function sanitizeContext(context: Record<string, any>): Record<string, any> {
  const sanitized: Record<string, any> = {};
  for (const [key, value] of Object.entries(context)) {
    if (value instanceof Blob) {
      // Replace blob with metadata instead
      sanitized[key] = {
        _type: "Blob",
        size: value.size,
        type: value.type,
      };
    } else if (value instanceof File) {
      // Replace File with metadata
      sanitized[key] = {
        _type: "File",
        name: value.name,
        size: value.size,
        type: value.type,
        lastModified: value.lastModified,
      };
    } else if (value && typeof value === "object" && !Array.isArray(value) && value.constructor === Object) {
      // Recursively sanitize nested plain objects
      sanitized[key] = sanitizeContext(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

/**
 * Capture IndexedDB errors in Sentry
 */
export function captureIndexDBError(
  operation: string,
  error: any,
  context?: {
    store?: string;
    key?: string | number;
    blobSize?: number;
    [key: string]: any;
  }
) {
  // Sanitize context to remove any Blob/File objects before sending to Sentry
  const sanitizedContext = context ? sanitizeContext(context) : {};
  
  Sentry.captureException(
    error instanceof Error ? error : new Error(String(error)),
    {
      tags: {
        context: "IndexDB",
        operation,
        error_type: error?.name || "Unknown",
        store: sanitizedContext?.store || "unknown",
      },
      extra: {
        operation,
        store: sanitizedContext?.store,
        key: sanitizedContext?.key,
        blobSize: sanitizedContext?.blobSize,
        error_message: error?.message,
        error_name: error?.name,
        ...sanitizedContext,
      },
    }
  );

  // Add breadcrumb for context
  Sentry.addBreadcrumb({
    category: "IndexDB",
    message: `IndexDB ${operation} failed`,
    level: "error",
    data: {
      operation,
      store: sanitizedContext?.store,
      error: error?.message || String(error),
      error_name: error?.name,
    },
  });
}
